import { API_CONFIG } from '../../config/api';

export interface FolderItem {
  id: string;
  name: string;
  description: string;
  createdBy: {
    id: string;
    firstName?: string;
    lastName?: string;
    preferredName?: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface FolderContentsResponse {
  success: boolean;
  data: {
    folder: FolderItem;
    breadcrumb: Array<{
      id: string;
      name: string;
    }>;
    childFolders: FolderItem[];
    childGroups: any[]; // Using any for now since GroupResponse is complex
  };
}

export interface CreateFolderRequest {
  name: string;
  description: string;
  parentFolderId?: string;
}

export interface CreateFolderResponse {
  success: boolean;
  message: string;
  data: FolderItem;
}

export interface RootFoldersResponse {
  success: boolean;
  data: {
    folders: FolderItem[];
  };
}

export interface ArchivedItemsResponse {
  success: boolean;
  data: {
    archivedFolders: FolderItem[];
    archivedGroups: any[]; // Keeping any for now (matches existing childGroups typing)
  };
}

/**
 * Create a new folder
 */
export const createFolder = async (
  token: string,
  folderData: CreateFolderRequest
): Promise<CreateFolderResponse> => {
  try {
    const response = await fetch(API_CONFIG.ENDPOINTS.GROUP_FOLDERS, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(folderData)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to create folder');
    }

    return data;
  } catch (error) {

    throw error;
  }
};

/**
 * Get root folders for the current consultant
 */
export const getRootFolders = async (token: string): Promise<RootFoldersResponse> => {
  try {
    const response = await fetch(`${API_CONFIG.ENDPOINTS.GROUP_FOLDERS}/root`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch root folders');
    }

    return data;
  } catch (error) {

    throw error;
  }
};

/**
 * Get folder contents including breadcrumb, child folders, and child groups
 */
export const getFolderContents = async (
  token: string,
  folderId: string
): Promise<FolderContentsResponse> => {
  try {
    const response = await fetch(`${API_CONFIG.ENDPOINTS.GROUP_FOLDERS}/${folderId}/contents`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch folder contents');
    }

    return data;
  } catch (error) {

    throw error;
  }
};

/**
 * Update folder metadata
 */
export const updateFolder = async (
  token: string,
  folderId: string,
  updateData: { name?: string; description?: string }
): Promise<{ success: boolean; message: string; data: FolderItem }> => {
  try {
    const response = await fetch(`${API_CONFIG.ENDPOINTS.GROUP_FOLDERS}/${folderId}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updateData)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to update folder');
    }

    return data;
  } catch (error) {

    throw error;
  }
};

/**
 * Delete a folder (archive)
 */
export const deleteFolder = async (
  token: string,
  folderId: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await fetch(`${API_CONFIG.ENDPOINTS.GROUP_FOLDERS}/${folderId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to delete folder');
    }

    return data;
  } catch (error) {

    throw error;
  }
};

/**
 * Restore an archived folder
 */
export const restoreFolder = async (
  token: string,
  folderId: string
): Promise<{ success: boolean; message: string; data: FolderItem }> => {
  try {
    const response = await fetch(`${API_CONFIG.ENDPOINTS.GROUP_FOLDERS}/${folderId}/restore`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to restore folder');
    }

    return data;
  } catch (error) {

    throw error;
  }
};

/**
 * Get archived folders + groups for the current consultant
 */
export const getArchivedItems = async (token: string): Promise<ArchivedItemsResponse> => {
  try {
    const response = await fetch(`${API_CONFIG.ENDPOINTS.GROUP_FOLDERS}/archived`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch archived items');
    }

    return data;
  } catch (error) {

    throw error;
  }
};

/**
 * Archive a folder (soft delete)
 */
export const archiveFolder = async (
  token: string,
  folderId: string
): Promise<{ success: boolean; message: string; data?: { folderId: string; archived: boolean } }> => {
  try {
    const response = await fetch(`${API_CONFIG.ENDPOINTS.GROUP_FOLDERS}/${folderId}/archive`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to archive folder');
    }

    return data;
  } catch (error) {

    throw error;
  }
};

/**
 * Hard delete a folder (cascade) with typed-name confirmation
 */
export const hardDeleteFolder = async (
  token: string,
  folderId: string,
  confirmName: string
): Promise<{
  success: boolean;
  message: string;
  data?: { deletedFolders: number; deletedGroups: number };
}> => {
  try {
    const response = await fetch(`${API_CONFIG.ENDPOINTS.GROUP_FOLDERS}/${folderId}/hard-delete`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ confirmName })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to hard delete folder');
    }

    return data;
  } catch (error) {

    throw error;
  }
};
